import React, { useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import type { KeyCalloutRef, Callout } from "../../models/API";

interface KeyCalloutsPickerProps {
  selected: KeyCalloutRef[];
  callouts: Callout[];
  onChange: (refs: KeyCalloutRef[]) => void;
  maxSelections?: number;
}

/**
 * KeyCalloutsPicker — lets the user select up to 3 callout references.
 * Each slot has two dropdowns: page_num selector, then card_number selector
 * filtered to callouts on that page (FR-4.1, FR-4.2, FR-4.3, FR-4.5).
 */
const KeyCalloutsPicker: React.FC<KeyCalloutsPickerProps> = ({
  selected,
  callouts,
  onChange,
  maxSelections = 3,
}) => {
  // Unique page numbers from callouts
  const pages = useMemo(() => {
    const pageSet = new Set(callouts.map((c) => c.page_num));
    return Array.from(pageSet).sort((a, b) => a - b);
  }, [callouts]);

  // Callouts grouped by page_num for the card selector
  const calloutsByPage = useMemo(() => {
    const map: Record<number, Callout[]> = {};
    for (const c of callouts) {
      if (!map[c.page_num]) map[c.page_num] = [];
      map[c.page_num].push(c);
    }
    // Sort each page's callouts by card_number
    for (const key of Object.keys(map)) {
      map[Number(key)].sort((a, b) => a.card_number - b.card_number);
    }
    return map;
  }, [callouts]);

  const handlePageChange = (index: number, pageNum: number) => {
    const updated = [...selected];
    // Reset card_number when page changes — pick first available
    const pageCallouts = calloutsByPage[pageNum] ?? [];
    const firstCard = pageCallouts.length > 0 ? pageCallouts[0].card_number : 1;
    updated[index] = {
      ...updated[index],
      page_num: pageNum,
      card_number: firstCard,
    };
    onChange(updated);
  };

  const handleCardChange = (index: number, cardNumber: number) => {
    const updated = [...selected];
    updated[index] = { ...updated[index], card_number: cardNumber };
    onChange(updated);
  };

  const handleAdd = () => {
    if (selected.length >= maxSelections) return;
    // Default to first page, first card
    const defaultPage = pages.length > 0 ? pages[0] : 1;
    const pageCallouts = calloutsByPage[defaultPage] ?? [];
    const defaultCard =
      pageCallouts.length > 0 ? pageCallouts[0].card_number : 1;
    onChange([
      ...selected,
      {
        __typename: "KeyCalloutRef",
        page_num: defaultPage,
        card_number: defaultCard,
      } as KeyCalloutRef,
    ]);
  };

  const handleRemove = (index: number) => {
    onChange(selected.filter((_, i) => i !== index));
  };

  /** Truncate title for display in the card selector */
  const truncate = (text: string, max: number = 40): string =>
    text.length > max ? text.slice(0, max) + "…" : text;

  return (
    <Box>
      {selected.map((ref, idx) => {
        const pageCallouts = calloutsByPage[ref.page_num] ?? [];
        return (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="body2" sx={{ minWidth: 60, color: "#64748B" }}>
              Slot {idx + 1}
            </Typography>
            <Select
              size="small"
              value={ref.page_num}
              onChange={(e) => handlePageChange(idx, Number(e.target.value))}
              sx={{ minWidth: 100 }}
              displayEmpty
            >
              {pages.map((p) => (
                <MenuItem key={p} value={p}>
                  Page {p}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={ref.card_number}
              onChange={(e) => handleCardChange(idx, Number(e.target.value))}
              sx={{ minWidth: 200 }}
              displayEmpty
            >
              {pageCallouts.map((c) => (
                <MenuItem key={c.card_number} value={c.card_number}>
                  #{c.card_number} — {truncate(c.title)}
                </MenuItem>
              ))}
            </Select>
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => handleRemove(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })}

      {callouts.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: "#94A3B8", fontStyle: "italic" }}
        >
          No callouts available to reference.
        </Typography>
      ) : (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={selected.length >= maxSelections}
          sx={{ mt: 0.5 }}
        >
          Add Key Callout ({selected.length}/{maxSelections})
        </Button>
      )}
    </Box>
  );
};

export default KeyCalloutsPicker;
